import { useState, useEffect, useCallback, useRef } from 'react';
import { FolderOpen, Plus, GripVertical, Pencil, Trash2, Check, X, ChevronUp, ChevronDown, Image, Frame as FrameIcon, Zap, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const CollectionsPanel = ({
  gallery,
  galleryId,
  axiosConfig,
  backendUrl,
  onRefreshGallery
}) => {
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingFrameFor, setSavingFrameFor] = useState(null);
  const [renderingFor, setRenderingFor] = useState(null);
  const [renderStatus, setRenderStatus] = useState(null);
  const renderPollRef = useRef(null);
  const [copiedLinkFor, setCopiedLinkFor] = useState(null);

  const collections = gallery?.collections || [];
  const frames = gallery?.frames || [];
  const frameById = Object.fromEntries(frames.map(f => [f.id, f]));
  const shareToken = gallery?.share_token;

  const handleCopyCollectionLink = async (collection) => {
    if (!shareToken) {
      toast.error('Gallery has no share token yet');
      return;
    }
    const url = `${window.location.origin}/share/${shareToken}/c/${collection.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkFor(collection.id);
      toast.success(`Link to "${collection.name}" copied`);
      setTimeout(() => setCopiedLinkFor(null), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCreate = async () => {
    if (!newCollectionName.trim()) return;

    try {
      setSaving(true);
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/collections`,
        { name: newCollectionName.trim() },
        axiosConfig
      );
      toast.success(`Collection "${newCollectionName.trim()}" created`);
      setNewCollectionName('');
      setIsAdding(false);
      onRefreshGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create collection');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (collectionId) => {
    if (!editingName.trim()) return;

    try {
      setSaving(true);
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/collections/${collectionId}`,
        { name: editingName.trim() },
        axiosConfig
      );
      toast.success('Collection renamed');
      setEditingId(null);
      setEditingName('');
      onRefreshGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename collection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collectionId) => {
    try {
      setSaving(true);
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/collections/${collectionId}`,
        axiosConfig
      );
      toast.success('Collection deleted. Photos moved to uncategorized.');
      setDeleteConfirm(null);
      onRefreshGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete collection');
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (index, direction) => {
    const newCollections = [...collections];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newCollections.length) return;

    [newCollections[index], newCollections[swapIndex]] = [newCollections[swapIndex], newCollections[index]];

    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/collections/reorder`,
        { collection_ids: newCollections.map(c => c.id) },
        axiosConfig
      );
      onRefreshGallery();
    } catch (error) {
      toast.error('Failed to reorder collections');
    }
  };

  const startEdit = (collection) => {
    setEditingId(collection.id);
    setEditingName(collection.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleAssignFrame = async (collectionId, rawValue) => {
    // rawValue is "" for "use default" (clear), otherwise a frame id
    const frame_id = rawValue === '' ? null : rawValue;
    setSavingFrameFor(collectionId);
    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/collections/${collectionId}`,
        { frame_id },
        axiosConfig
      );
      toast.success(frame_id ? 'Frame assigned' : 'Frame cleared (using gallery default)');
      onRefreshGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update frame assignment');
    } finally {
      setSavingFrameFor(null);
    }
  };

  const fetchRenderStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/frames/render-status`,
        axiosConfig
      );
      setRenderStatus(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, [backendUrl, galleryId, axiosConfig]);

  // Pick up any in-progress render when the panel mounts or the gallery changes.
  useEffect(() => {
    fetchRenderStatus();
  }, [gallery?.id, fetchRenderStatus]);

  // Poll while a collection render is processing.
  useEffect(() => {
    if (renderStatus?.status === 'processing' && renderStatus?.collection_id) {
      renderPollRef.current = setInterval(async () => {
        const status = await fetchRenderStatus();
        if (status && status.status !== 'processing') {
          clearInterval(renderPollRef.current);
          if (status.status === 'complete') {
            const succeeded = (status.progress || 0) - (status.errors || 0);
            toast.success(`Pre-rendering complete — ${succeeded} photos rendered.`);
          }
        }
      }, 3000);
      return () => clearInterval(renderPollRef.current);
    }
  }, [renderStatus?.status, renderStatus?.collection_id, fetchRenderStatus]);

  const handlePreRenderCollection = async (collection) => {
    setRenderingFor(collection.id);
    try {
      const res = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/frames/pre-render`,
        { collection_id: collection.id },
        axiosConfig
      );
      toast.success(res.data?.message || `Pre-rendering "${collection.name}" in background`);
      // Kick off polling immediately so the progress bar appears without waiting for the next tick.
      await fetchRenderStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start pre-rendering');
    } finally {
      setRenderingFor(null);
    }
  };

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#BF212F] rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20">
            <FolderOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">Collections</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Organize photos into groups
            </p>
          </div>
        </div>
        {!isAdding && (
          <Button
            onClick={() => setIsAdding(true)}
            className="bg-[#BF212F] text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Collection
          </Button>
        )}
      </div>

      {/* Add Collection Form */}
      {isAdding && (
        <Card className="mb-4 p-4 border-2 border-dashed border-[#BF212F]/30">
          <div className="flex items-center gap-3">
            <Input
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              placeholder="Collection name (e.g., Day One, Reception)"
              className="flex-1"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') { setIsAdding(false); setNewCollectionName(''); }
              }}
            />
            <Button
              onClick={handleCreate}
              disabled={!newCollectionName.trim() || saving}
              size="sm"
              className="bg-[#BF212F] text-white"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => { setIsAdding(false); setNewCollectionName(''); }}
              variant="ghost"
              size="sm"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Collections List */}
      {collections.length === 0 ? (
        <Card className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
            No collections yet
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Create collections to organize photos into groups like "Day One", "Reception", etc.
          </p>
          {!isAdding && (
            <Button
              onClick={() => setIsAdding(true)}
              variant="outline"
              className="border-[#BF212F] text-[#BF212F] hover:bg-[#BF212F] hover:text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Collection
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {collections.map((collection, index) => {
            const assignedFrame = collection.frame_id ? frameById[collection.frame_id] : null;
            return (
            <Card
              key={collection.id}
              className="p-3 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3">
                {/* Drag handle / reorder */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => handleReorder(index, 'up')}
                    disabled={index === 0}
                    className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleReorder(index, 'down')}
                    disabled={index === collections.length - 1}
                    className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Icon */}
                <div className="w-9 h-9 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                </div>

                {/* Name (editable) */}
                <div className="flex-1 min-w-0">
                  {editingId === collection.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(collection.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <Button
                        onClick={() => handleRename(collection.id)}
                        disabled={!editingName.trim() || saving}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button onClick={cancelEdit} size="sm" variant="ghost" className="h-8 w-8 p-0">
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 dark:text-white truncate">
                        {collection.name}
                      </span>
                      <Badge variant="secondary" className="text-xs font-semibold">
                        <Image className="w-3 h-3 mr-1" />
                        {collection.photo_count || 0}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {editingId !== collection.id && (
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => handleCopyCollectionLink(collection)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-[#BF212F]"
                      title="Copy direct link to this collection"
                      disabled={!shareToken}
                    >
                      {copiedLinkFor === collection.id
                        ? <Check className="w-4 h-4 text-green-600" />
                        : <LinkIcon className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={() => startEdit(collection)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteConfirm(collection)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Branded frame assignment */}
              {frames.length > 0 && (() => {
                const isRenderingThis =
                  renderStatus?.status === 'processing' &&
                  renderStatus?.collection_id === collection.id;
                const progress = renderStatus?.progress || 0;
                const total = renderStatus?.total || 0;
                const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
                return (
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <div className="flex flex-wrap items-center gap-2">
                      <FrameIcon className="w-4 h-4 text-slate-400" />
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Frame:</span>
                      <select
                        value={collection.frame_id || ''}
                        onChange={(e) => handleAssignFrame(collection.id, e.target.value)}
                        disabled={savingFrameFor === collection.id}
                        className="text-xs h-7 px-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#BF212F]/40"
                      >
                        <option value="">— Use gallery default —</option>
                        {frames.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                      {assignedFrame && (
                        <Badge variant="secondary" className="text-xs">
                          {assignedFrame.name}
                        </Badge>
                      )}
                      {assignedFrame && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs ml-auto"
                          onClick={() => handlePreRenderCollection(collection)}
                          disabled={renderingFor === collection.id || isRenderingThis || (collection.photo_count || 0) === 0}
                        >
                          {renderingFor === collection.id || isRenderingThis
                            ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            : <Zap className="w-3 h-3 mr-1" />}
                          {isRenderingThis ? 'Rendering…' : 'Pre-render'}
                        </Button>
                      )}
                    </div>
                    {isRenderingThis && (
                      <div className="mt-3 space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-600 dark:text-slate-300">
                            Pre-rendering {progress} of {total} photos
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">{pct}%</span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-[#BF212F] h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </Card>
          );
          })}
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-6 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 text-sm md:text-base">
          About Collections
        </h4>
        <ul className="text-xs md:text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Collections help organize photos into groups (e.g., "Day One", "Reception")</li>
          <li>• Select a collection when uploading to automatically assign photos</li>
          <li>• Guests can browse by collection on the public gallery</li>
          <li>• Deleting a collection moves its photos to uncategorized</li>
        </ul>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Collection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"?
              {deleteConfirm?.photo_count > 0 && (
                <span className="block mt-2 font-medium text-amber-600">
                  {deleteConfirm.photo_count} photo(s) will be moved to uncategorized.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm?.id)}
              disabled={saving}
            >
              {saving ? 'Deleting...' : 'Delete Collection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionsPanel;
